import type { SpringConfig } from '@react-spring/web'
import type { Variants } from 'framer-motion'

/**
 * Consistent easing curve matching existing patterns in the codebase
 * Smooth, natural motion: cubic-bezier(0.22, 1, 0.36, 1)
 */
export const easingCurve = [0.22, 1, 0.36, 1] as const

/**
 * Animation durations (in seconds)
 */
export const durations = {
	fast: 0.2,
	normal: 0.3,
	slow: 0.5,
	pageEntrance: 0.6,
} as const

/**
 * Framer Motion animation variants for common use cases
 */
export const fadeIn: Variants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			duration: durations.normal,
			ease: easingCurve,
		},
	},
}

export const slideUp: Variants = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: durations.normal,
			ease: easingCurve,
		},
	},
}

export const slideDown: Variants = {
	hidden: { opacity: 0, y: -10 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: durations.fast,
			ease: easingCurve,
		},
	},
}

export const slideLeft: Variants = {
	hidden: { opacity: 0, x: 20 },
	visible: {
		opacity: 1,
		x: 0,
		transition: {
			duration: durations.normal,
			ease: easingCurve,
		},
	},
}

export const scaleIn: Variants = {
	hidden: { opacity: 0, scale: 0.95 },
	visible: {
		opacity: 1,
		scale: 1,
		transition: {
			duration: durations.normal,
			ease: easingCurve,
		},
	},
}

/**
 * Page entrance animation - combines fade and slide up
 */
export const pageEntrance: Variants = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: durations.pageEntrance,
			ease: easingCurve,
		},
	},
}

/**
 * Stagger container variant for sequential child animations
 */
export const staggerContainer: Variants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.05,
			delayChildren: 0.1,
		},
	},
}

/**
 * Stagger child variant - used with staggerContainer
 */
export const staggerChild: Variants = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: durations.normal,
			ease: easingCurve,
		},
	},
}

/**
 * React Spring configuration presets
 * These provide natural spring physics for micro-interactions
 */

/**
 * Default spring config - smooth and natural
 * Good for: general animations, button interactions
 */
export const defaultSpring: SpringConfig = {
	tension: 300,
	friction: 30,
}

/**
 * Gentle spring - subtle and smooth
 * Good for: form field focus, label animations
 */
export const gentleSpring: SpringConfig = {
	tension: 200,
	friction: 25,
}

/**
 * Snappy spring - quick and responsive
 * Good for: button presses, quick feedback
 */
export const snappySpring: SpringConfig = {
	tension: 400,
	friction: 25,
}

/**
 * Smooth spring - very fluid motion
 * Good for: error messages, loading states
 */
export const smoothSpring: SpringConfig = {
	tension: 250,
	friction: 30,
}

/**
 * Bouncy spring - playful and bouncy motion
 * Good for: tab switches, toggle buttons, interactive elements
 */
export const bouncySpring: SpringConfig = {
	tension: 350,
	friction: 20,
}

/**
 * Check if user prefers reduced motion
 * Respects prefers-reduced-motion media query
 */
export function prefersReducedMotion(): boolean {
	if (typeof window === 'undefined') return false
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}
